import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function AnnuitiesRetirementPlanningIndiana() {
  const SITE_URL = 'https://hoosierlifeinsurance.com';

  useEffect(() => {
    // Set page title and meta tags
    document.title = 'Annuities & Retirement Planning | Indiana Families';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Secure your retirement with annuities designed for Indiana families. Learn how to create guaranteed income streams that protect your future—no matter what the market does.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'annuities Indiana, retirement planning, guaranteed income, Indianapolis retirement, fixed annuities');
    }

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/resources/annuities-retirement-planning-indiana`;

    // Inject Schema.org JSON-LD
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Annuities & Retirement Planning for Indiana Families',
        description: 'Secure your retirement with annuities designed for Indiana families. Learn how to create guaranteed income streams that protect your future—no matter what the market does.',
        author: {
          '@type': 'Person',
          name: 'Benjamin Wright'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          url: SITE_URL
        },
        datePublished: '2025-01-18T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/resources/annuities-retirement-planning-indiana`,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/resources/annuities-retirement-planning-indiana`
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Resources',
            item: `${SITE_URL}/resources`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Annuities & Retirement Planning for Indiana Families',
            item: `${SITE_URL}/resources/annuities-retirement-planning-indiana`
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Annuities & Retirement Planning for Indiana Families',
        description: 'Secure your retirement with annuities designed for Indiana families. Learn how to create guaranteed income streams that protect your future—no matter what the market does.',
        url: `${SITE_URL}/resources/annuities-retirement-planning-indiana`,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL,
          name: 'Hoosier Life Insurance'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is an annuity and how does it work for Indiana retirees?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'An annuity is a contract between you and an insurance company. You pay a lump sum (or series of payments), and in return the insurance company guarantees to pay you income—either immediately or at a future date. Think of it as a personal pension: you give the insurance company money today, and they pay you a fixed amount every month for life. Annuities solve three core retirement problems for Indiana retirees: longevity risk (running out of money before you die), market risk (losing savings in a stock market crash), and income gap (filling the space between Social Security payments and your actual monthly expenses).'
            }
          },
          {
            '@type': 'Question',
            name: 'What type of annuity is best for Indiana retirees?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Fixed annuities are the most popular choice for Indiana retirees who want guaranteed income with zero market risk. They grow at a guaranteed interest rate (3-5% in 2025) and provide fixed monthly payments for life. Fixed indexed annuities offer higher growth potential by tying returns to an index like the S&P 500, with a 0% floor so you cannot lose principal. Variable annuities have unlimited growth potential but also carry market risk. For most conservative Indiana retirees, fixed or fixed indexed annuities are the best fit depending on risk tolerance and retirement timeline.'
            }
          },
          {
            '@type': 'Question',
            name: 'How much monthly income can I get from an annuity in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Monthly income from a $100,000 immediate fixed annuity (single life, no death benefit) in Indiana depends on your age: Age 60 receives approximately $475/month (5.7% payout rate), age 65 receives $550/month (6.6%), age 70 receives $650/month (7.8%), age 75 receives $775/month (9.3%), and age 80 receives $950/month (11.4%). Joint life payouts covering both spouses pay approximately 10-15% less. These rates were updated January 2026.'
            }
          },
          {
            '@type': 'Question',
            name: 'Are annuity payments taxed in Indiana?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Indiana does not tax annuity income differently from other retirement income. Your money grows tax-deferred inside the annuity—you only pay taxes when you withdraw income. Indiana\'s flat 3.05% state income tax applies to annuity withdrawals, and federal income tax also applies. Indiana does not tax Social Security benefits, making combined Social Security plus annuity income more tax-efficient than in many other states. Death benefits from annuities pass to beneficiaries income tax-free.'
            }
          },
          {
            '@type': 'Question',
            name: 'What are the most common annuity mistakes Indiana retirees make?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'The top 5 annuity mistakes Indiana retirees make are: (1) Putting all savings into an annuity—keep 30-50% liquid for emergencies and healthcare. (2) Buying from the first agent without comparing at least 3 quotes, since rates vary widely by carrier. (3) Ignoring inflation—fixed payments lose purchasing power over time, so consider keeping some investments in stocks. (4) Buying too early before age 60, since payout rates increase significantly with age. (5) Not understanding surrender charges—most annuities have 5-10 year surrender periods with early withdrawal penalties of 5-10%.'
            }
          }
        ]
      }
    ];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-resource-annuities-${index}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-resource-annuities-${index}`);
        if (script) {
          document.head.removeChild(script);
        }
      });
    };
  }, [SITE_URL]);

  return (
    <>
      <title>Annuities &amp; Retirement Planning for Indiana Families | Hoosier Life Insurance</title>
      <article className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="mb-6">
              <Link to="/resources" className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm">
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Resources
              </Link>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Annuities for Retirement Planning in Indiana — Guaranteed Income Guide</h1>
            <p className="text-xl text-white/80 mb-8">How to create guaranteed lifetime income and protect your retirement savings from market volatility</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span><i className="ri-calendar-line mr-2"></i>Updated January 2026</span>
              <span><i className="ri-time-line mr-2"></i>12 min read</span>
              <span><i className="ri-map-pin-line mr-2"></i>Indiana Retirement Guide</span>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              You've spent 30+ years saving for retirement. Now comes the hardest question: <strong>How do you turn that nest egg into reliable income that lasts the rest of your life?</strong>
            </p>
            <p className="text-gray-700 leading-relaxed">
              For many Indiana retirees, annuities are the answer. They provide guaranteed income you can't outlive, protect your savings from market crashes, and offer tax-deferred growth. This guide explains how annuities work, which types are best for Indiana retirees, and how to use them as part of a complete retirement strategy.
            </p>
          </div>

          {/* Quick Answer */}
          <div className="bg-[#FDF6F0] border-l-4 border-[#D97642] p-6 mb-12 rounded-r-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <i className="ri-lightbulb-line text-[#D97642] mr-2 text-2xl"></i>
              Quick Answer for Indiana Retirees
            </h3>
            <p className="text-gray-800 leading-relaxed">
              <strong>Annuities are insurance contracts that convert a lump sum into guaranteed lifetime income.</strong> Think of them as a personal pension—you give the insurance company money today, and they pay you a fixed amount every month for life (or a set number of years). Best for retirees who want predictable income and protection from market volatility.
            </p>
          </div>

          {/* What Is an Annuity */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What Is an Annuity?</h2>
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                An annuity is a contract between you and an insurance company. You pay a lump sum (or series of payments), and in return, the insurance company guarantees to pay you income—either immediately or at a future date.
              </p>
              
              <div className="bg-teal-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Simple Example</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      1
                    </div>
                    <p className="text-gray-700 pt-2">You give the insurance company $200,000 at age 65</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      2
                    </div>
                    <p className="text-gray-700 pt-2">They guarantee to pay you $1,200/month for life</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                      3
                    </div>
                    <p className="text-gray-700 pt-2">You receive that $1,200 every month until you die—even if you live to 100</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-teal-500 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-check-line text-teal-600 mr-3 text-2xl"></i>
                  What Annuities Solve
                </h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Longevity risk:</strong> Running out of money before you die</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Market risk:</strong> Losing savings in a stock market crash</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Income gap:</strong> Filling the gap between Social Security and expenses</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Pension replacement:</strong> Creating your own guaranteed income stream</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-information-line text-gray-600 mr-3 text-2xl"></i>
                  What Annuities Don't Solve
                </h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-gray-600 mr-2 mt-1"></i>
                    <span><strong>Liquidity:</strong> Money is locked up (early withdrawal penalties)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-gray-600 mr-2 mt-1"></i>
                    <span><strong>Growth potential:</strong> Lower returns than stocks over long term</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-gray-600 mr-2 mt-1"></i>
                    <span><strong>Inflation protection:</strong> Fixed payments lose purchasing power over time</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-gray-600 mr-2 mt-1"></i>
                    <span><strong>Legacy:</strong> Less money left for heirs (unless you add riders)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Types of Annuities */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">3 Types of Annuities for Indiana Retirees</h2>
            
            <div className="space-y-6">
              {/* Fixed Annuities */}
              <div className="bg-white border-2 border-teal-500 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-lock-line text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">1. Fixed Annuities (Most Popular for Indiana Retirees)</h3>
                    <p className="text-gray-700 mb-4">
                      <strong>How it works:</strong> You deposit money, it grows at a guaranteed interest rate (3-5% in 2025), and you receive fixed monthly payments for life.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="font-bold text-gray-900 mb-2">✅ Pros:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                            <span>100% principal protection (no market risk)</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                            <span>Guaranteed interest rate</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                            <span>Predictable income you can't outlive</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                            <span>Tax-deferred growth</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 mb-2">❌ Cons:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Lower returns than stocks</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Fixed payments don't adjust for inflation</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Surrender charges if you withdraw early (5-10 years)</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-[#FDF6F0] rounded-lg p-4">
                      <p className="text-sm text-gray-700"><strong>Best for:</strong> Conservative Indiana retirees who want guaranteed income and zero market risk. Ideal for covering essential expenses (housing, food, healthcare).</p>
                      <p className="text-sm text-gray-700 mt-2"><strong>Example:</strong> $250,000 at age 65 = $1,400-$1,600/month for life</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fixed Indexed Annuities */}
              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">2. Fixed Indexed Annuities (Growth + Protection)</h3>
                    <p className="text-gray-700 mb-4">
                      <strong>How it works:</strong> Your returns are tied to a stock market index (like S&P 500), but your principal is protected. You get market gains (up to a cap) without market losses.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="font-bold text-gray-900 mb-2">✅ Pros:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Principal protected (can't lose money)</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Higher growth potential than fixed annuities</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Tax-deferred growth</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Guaranteed lifetime income option</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 mb-2">❌ Cons:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Returns capped (e.g., 6-8% max even if market gains 15%)</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Complex formulas (participation rates, caps, spreads)</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Longer surrender periods (7-10 years)</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700"><strong>Best for:</strong> Indiana retirees who want growth potential but can't stomach market losses. Good for accumulation phase (ages 55-65) before converting to income.</p>
                      <p className="text-sm text-gray-700 mt-2"><strong>Example:</strong> $200,000 at age 60 could grow to $280,000-$320,000 by age 70 (depending on market performance)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Variable Annuities */}
              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-stock-line text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">3. Variable Annuities (Highest Risk/Reward)</h3>
                    <p className="text-gray-700 mb-4">
                      <strong>How it works:</strong> You invest in mutual fund-like sub-accounts. Your returns (and principal) fluctuate with the market. You can add riders for guaranteed income.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="font-bold text-gray-900 mb-2">✅ Pros:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Unlimited growth potential</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Tax-deferred growth</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Optional guaranteed income riders</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-check-line text-gray-600 mr-2 mt-1"></i>
                            <span>Death benefit for heirs</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 mb-2">❌ Cons:</p>
                        <ul className="space-y-1 text-sm text-gray-700">
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Can lose principal in market downturns</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>High fees (2-3% annually)</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Complex and hard to understand</span>
                          </li>
                          <li className="flex items-start">
                            <i className="ri-close-line text-red-500 mr-2 mt-1"></i>
                            <span>Expensive riders for guarantees</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-700"><strong>Best for:</strong> Wealthy Indiana retirees with high risk tolerance who want tax-deferred growth and can afford to lose principal. Not recommended for most retirees.</p>
                      <p className="text-sm text-gray-700 mt-2"><strong>Warning:</strong> Variable annuities are the most expensive and complex option. Most Indiana retirees are better served by fixed or fixed indexed annuities.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Immediate vs Deferred */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Immediate vs. Deferred Annuities</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-teal-500 rounded-xl p-6">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-play-circle-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Immediate Annuities</h3>
                <p className="text-gray-700 mb-4 text-sm">
                  <strong>When income starts:</strong> Within 1 year of purchase
                </p>
                <p className="text-gray-700 mb-4 text-sm">
                  <strong>How it works:</strong> You give the insurance company a lump sum, and they start paying you immediately (or within 12 months).
                </p>
                <p className="text-gray-700 mb-4 text-sm">
                  <strong>Best for:</strong> Indiana retirees who need income NOW (ages 65+)
                </p>
                <div className="bg-teal-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700"><strong>Example:</strong> Age 67, deposit $300,000, receive $1,800/month starting next month for life</p>
                </div>
              </div>

              <div className="bg-white border-2 border-gray-300 rounded-xl p-6">
                <div className="w-12 h-12 bg-gray-600 text-white rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-time-line text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Deferred Annuities</h3>
                <p className="text-gray-700 mb-4 text-sm">
                  <strong>When income starts:</strong> Years or decades in the future
                </p>
                <p className="text-gray-700 mb-4 text-sm">
                  <strong>How it works:</strong> Your money grows tax-deferred during the accumulation phase, then converts to income later.
                </p>
                <p className="text-gray-700 mb-4 text-sm">
                  <strong>Best for:</strong> Pre-retirees who want to grow savings before retirement (ages 50-64)
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700"><strong>Example:</strong> Age 55, deposit $200,000, let it grow to $320,000 by age 70, then start $2,100/month income</p>
                </div>
              </div>
            </div>
          </div>

          {/* Real Indiana Example */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Real Indiana Example: How Bob Used Annuities to Retire Confidently</h2>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Bob Thompson, Age 66, Carmel</h3>
                  <p className="text-gray-700">Retired Cummins engineer, married, no pension</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Bob's Situation</h4>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Retirement savings:</strong> $650,000 in 401(k) and IRAs</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Social Security:</strong> $2,400/month (Bob) + $1,200/month (wife)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Monthly expenses:</strong> $6,500/month</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-s-line text-teal-600 mr-2 mt-1"></i>
                    <span><strong>Income gap:</strong> $2,900/month ($6,500 expenses - $3,600 Social Security)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-teal-600 text-white rounded-xl p-6 mb-6">
                <h4 className="text-xl font-bold mb-4">Bob's Annuity Strategy</h4>
                <div className="space-y-4">
                  <div>
                    <p className="font-bold mb-2">Step 1: Cover Essential Expenses with Fixed Annuity</p>
                    <p className="text-teal-50 text-sm">Purchased $400,000 immediate fixed annuity → $2,400/month guaranteed for life</p>
                  </div>
                  <div>
                    <p className="font-bold mb-2">Step 2: Keep Growth Portfolio for Discretionary Spending</p>
                    <p className="text-teal-50 text-sm">Kept $250,000 in diversified stock/bond portfolio for travel, gifts, emergencies</p>
                  </div>
                  <div>
                    <p className="font-bold mb-2">Result: Guaranteed Income Covers All Essential Expenses</p>
                    <p className="text-teal-50 text-sm">Social Security ($3,600) + Annuity ($2,400) = $6,000/month guaranteed. Market crashes can't touch it.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6">
                <p className="text-gray-700 italic leading-relaxed">
                  "I sleep better knowing that no matter what happens to the stock market, my wife and I have $6,000/month guaranteed for life. We can cover our mortgage, utilities, food, and healthcare without touching our investment portfolio. That $250,000 we kept invested is for fun—travel, grandkids, hobbies. If the market crashes, we don't have to sell at a loss because we're not dependent on it for income."
                </p>
                <p className="text-sm text-gray-600 mt-4 text-right">— Bob Thompson, Carmel, IN</p>
              </div>
            </div>
          </div>

          {/* Annuity Payout Rates */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What Indiana Retirees Actually Receive (2025 Rates)</h2>
            <p className="text-gray-700 mb-8">
              Here's how much monthly income you can expect from a $100,000 immediate fixed annuity (single life, no death benefit):
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-[#1A3A52] text-white">
                    <th className="px-6 py-4 text-left font-bold">Age</th>
                    <th className="px-6 py-4 text-left font-bold">Monthly Income</th>
                    <th className="px-6 py-4 text-left font-bold">Annual Income</th>
                    <th className="px-6 py-4 text-left font-bold">Payout Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 60</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$475/month</td>
                    <td className="px-6 py-4 text-gray-700">$5,700/year</td>
                    <td className="px-6 py-4 text-gray-700">5.7%</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 65</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$550/month</td>
                    <td className="px-6 py-4 text-gray-700">$6,600/year</td>
                    <td className="px-6 py-4 text-gray-700">6.6%</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 70</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$650/month</td>
                    <td className="px-6 py-4 text-gray-700">$7,800/year</td>
                    <td className="px-6 py-4 text-gray-700">7.8%</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 75</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$775/month</td>
                    <td className="px-6 py-4 text-gray-700">$9,300/year</td>
                    <td className="px-6 py-4 text-gray-700">9.3%</td>
                  </tr>
                  <tr className="hover:bg-[#F9F7F4]">
                    <td className="px-6 py-4 font-bold text-gray-900">Age 80</td>
                    <td className="px-6 py-4 text-[#D97642] font-bold">$950/month</td>
                    <td className="px-6 py-4 text-gray-700">$11,400/year</td>
                    <td className="px-6 py-4 text-gray-700">11.4%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600 mt-4">*Rates for Indiana residents, single life payout, no death benefit. Joint life (covers both spouses) pays 10-15% less. Rates updated January 2026.</p>
          </div>

          {/* Pros and Cons */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Should You Use Annuities in Your Indiana Retirement Plan?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-teal-500 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-thumb-up-line text-teal-600 mr-3 text-2xl"></i>
                  When Annuities Make Sense
                </h3>
                <ul className="space-y-3 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                    <span>You want guaranteed income you can't outlive</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                    <span>You're worried about market volatility in retirement</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                    <span>You don't have a pension and need to create one</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                    <span>You have longevity in your family (parents lived to 90+)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                    <span>You want to cover essential expenses with guaranteed income</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                    <span>You're not good at managing investments yourself</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white border-2 border-red-500 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-thumb-down-line text-red-500 mr-3 text-2xl"></i>
                  When to Avoid Annuities
                </h3>
                <ul className="space-y-3 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-close-line text-red-500 mr-3 mt-1 text-xl"></i>
                    <span>You need access to your money (liquidity)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line text-red-500 mr-3 mt-1 text-xl"></i>
                    <span>You want to leave a large inheritance to your kids</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line text-red-500 mr-3 mt-1 text-xl"></i>
                    <span>You have serious health issues (short life expectancy)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line text-red-500 mr-3 mt-1 text-xl"></i>
                    <span>You're comfortable managing market risk yourself</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line text-red-500 mr-3 mt-1 text-xl"></i>
                    <span>You already have enough guaranteed income (pension + Social Security)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line text-red-500 mr-3 mt-1 text-xl"></i>
                    <span>You're under age 55 (too early for most annuities)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Indiana Tax Benefits */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Indiana Tax Benefits of Annuities</h2>
            <div className="bg-teal-50 rounded-xl p-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Tax-Deferred Growth</h3>
                  <p className="text-gray-700 text-sm mb-4">
                    Your money grows without paying taxes each year. You only pay taxes when you withdraw income—potentially at a lower tax rate in retirement.
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Example:</strong> $200,000 growing at 5% for 10 years = $325,779 tax-deferred vs. $295,000 in a taxable account (assuming 25% tax rate).
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">No Indiana State Income Tax on Annuity Income</h3>
                  <p className="text-gray-700 text-sm mb-4">
                    Indiana doesn't tax annuity income differently than other retirement income. You'll pay federal income tax on withdrawals, but Indiana's flat 3.05% state tax applies to all income equally.
                  </p>
                  <p className="text-gray-700 text-sm">
                    <strong>Bonus:</strong> Indiana doesn't tax Social Security benefits, so your combined Social Security + annuity income is more tax-efficient than in many other states.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Common Mistakes */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">5 Annuity Mistakes Indiana Retirees Make</h2>
            <div className="space-y-4">
              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #1: Putting All Your Money in an Annuity
                </h3>
                <p className="text-gray-700 text-sm">
                  Never annuitize 100% of your savings. Keep 30-50% liquid for emergencies, healthcare, and legacy. Use annuities to cover essential expenses only.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #2: Buying an Annuity from the First Agent You Meet
                </h3>
                <p className="text-gray-700 text-sm">
                  Annuity rates and features vary widely. Shop around and compare at least 3 quotes from different carriers before committing.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #3: Ignoring Inflation
                </h3>
                <p className="text-gray-700 text-sm">
                  Fixed annuity payments don't increase with inflation. $2,000/month today will feel like $1,200/month in 20 years. Consider inflation riders or keep part of your portfolio in stocks.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #4: Buying Too Early (Before Age 60)
                </h3>
                <p className="text-gray-700 text-sm">
                  Annuity payout rates increase with age. Buying at 55 vs. 65 can cost you 15-20% in lifetime income. Wait until you're closer to retirement unless you have a specific reason.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  <i className="ri-close-circle-line text-red-500 mr-3"></i>
                  Mistake #5: Not Understanding Surrender Charges
                </h3>
                <p className="text-gray-700 text-sm">
                  Most annuities have surrender periods (5-10 years) where early withdrawals trigger penalties of 5-10%. Make sure you won't need the money during that time.
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Explore Annuities for Your Indiana Retirement?</h2>
            <p className="text-xl text-white/80 mb-8">
              Let's review your retirement income needs and see if an annuity makes sense for your situation. Free consultation, no obligation.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link 
                to="/annuities" 
                className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Learn More About Annuities
              </Link>
              <Link 
                to="/book-appointment" 
                className="bg-[#0F2A3F] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap"
              >
                Schedule a Consultation
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>

            {/* Product Pages — two-way authority signal */}
            <div className="mb-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Product Pages</p>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <Link to="/annuities" className="group bg-white border-2 border-[#D97642] rounded-xl p-5 hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-funds-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Annuities in Indiana</h3>
                  <p className="text-xs text-gray-500">Get your free annuity quote and income estimate</p>
                </Link>
                <Link to="/indexed-universal-life" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-line-chart-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Indexed Universal Life (IUL)</h3>
                  <p className="text-xs text-gray-500">Market-linked growth with downside protection</p>
                </Link>
                <Link to="/generational-wealth" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-hand-coin-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Generational Wealth Planning</h3>
                  <p className="text-xs text-gray-500">Build wealth and transfer it tax-free to heirs</p>
                </Link>
              </div>
            </div>

            {/* Articles */}
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Articles &amp; Guides</p>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/blog/annuities-retirement-indianapolis" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-article-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Annuities for Retirement in Indianapolis</h3>
                <p className="text-sm text-gray-600">How Indianapolis retirees are using annuities to build guaranteed income</p>
              </Link>

              <Link to="/whole-life" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-infinity-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Whole Life Insurance</h3>
                <p className="text-sm text-gray-600">Permanent coverage with guaranteed cash value growth</p>
              </Link>

              <Link to="/blog/wealth-building-life-insurance-indianapolis" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-bank-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Wealth Building with Life Insurance</h3>
                <p className="text-sm text-gray-600">Using IUL and whole life as tax-free wealth building tools</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions About Annuities and Retirement Income?</h2>
            <p className="text-xl text-gray-700 mb-8">
              Our Indiana-based retirement specialists help retirees create guaranteed income strategies every day. Let's build a plan that works for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3176482983" className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0F2A3F] transition-colors inline-flex items-center justify-center">
                <i className="ri-phone-line mr-2"></i>
                Call (317) 648-2983
              </a>
              <Link to="/contact" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-message-3-line mr-2"></i>
                Send a Message
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}